import { useFlags } from 'launchdarkly-react-client-sdk';
import { cloneDeep } from 'lodash';
import { useEffect, useState, useMemo } from 'react';
import { useSnapshot } from 'valtio';
import useScreenCepActivitiesConfig from './MiViewScreenCepActivities.config';
import { TacticStatus } from 'basics/enums/maya.enums';
import useGetAllTacticsQuery from 'basics/graphql/queries/getAllTactics';
import useGetTargetListByCepIdQuery from 'basics/graphql/queries/getTargetListByCepId';
import { transformTacticTitle } from 'basics/transformers/Tactic.transformer';
import { CepStateType } from 'basics/types/maya.types';
import { Plan } from 'components/MiViewPreview/MiViewPlanSelector/MiViewPlanSelector.types';
import { Target, Tactic } from 'generated/maya.types';
import cepState from 'states/cep.states';

const useMiViewScreenCepActivities = (fullScreen: boolean, setFullScreen: Function) => {
  const cepStateValue = useSnapshot(cepState) as CepStateType;
  const flags = useFlags();
  const [targets, setTargets] = useState<Target[]>([]);
  const [tactics, setTactics] = useState<Tactic[]>([]);
  const [screenLoading, setScreenLoading] = useState<boolean>(true);
  const [selectedAccount, setSelectedAccount] = useState<Target | undefined>(undefined);
  const [openedAccordion, setOpenedAccordion] = useState<boolean>(true);
  const [renderKey, setRenderKey] = useState<string>(crypto.randomUUID());

  const collapseAllAction = () => {
    setOpenedAccordion(false);
    setRenderKey(crypto.randomUUID());
  };

  const expandAllAction = () => {
    setOpenedAccordion(true);
    setRenderKey(crypto.randomUUID());
  };

  const fullScreenAction = () => setFullScreen(true);
  const minimizeScreenAction = () => setFullScreen(false);

  const { screenCepTargetsConfig } = useScreenCepActivitiesConfig(
    collapseAllAction,
    expandAllAction,
    fullScreenAction,
    minimizeScreenAction,
    fullScreen,
  );

  const cepId = cepStateValue.cep?._id ? cepStateValue.cep._id : null;
  const plans: Plan[] = [
    {
      id: cepStateValue.cep?._id ? cepStateValue.cep._id : null,
      name: cepStateValue.cep?.title ? cepStateValue.cep.title : null,
    },
  ];

  const defaultPlan: Plan = {
    id: cepStateValue.cep?._id ? cepStateValue.cep._id : null,
    name: cepStateValue.cep?.title ? cepStateValue.cep.title : null,
  };

  const cep = cepStateValue?.cep;

  const { data, loading: targetsLoading } = useGetTargetListByCepIdQuery(cepId);

  const queryTacticFilters = useMemo(
    () => ({
      cep: cepId,
      status: [TacticStatus.EMPTY, TacticStatus.DRAFT, TacticStatus.APPROVED],
    }),
    [cepId],
  );

  const { data: tacticsData, loading: tacticsLoading } = useGetAllTacticsQuery(undefined, queryTacticFilters);

  useEffect(() => {
    if (!targetsLoading) {
      const targetsList = cloneDeep(data);

      const sortedTargets = targetsList.sort((prevTarget, nextTarget) => {
        return `${prevTarget.firstName || prevTarget.lastName}`
          .localeCompare(`${nextTarget.firstName || nextTarget.lastName}`);
      });

      setTargets(sortedTargets);
    }

    if (!tacticsLoading) {
      const tacticsList = cloneDeep(tacticsData);
      const sortedTactics = tacticsList.sort((prevTactic, nextTactic) => Number(prevTactic.actionOrder) - Number(nextTactic.actionOrder));
      const transformedTactics = sortedTactics.map((tactic) => transformTacticTitle(tactic, flags));
      setTactics(transformedTactics);
    }
  }, [data, targetsLoading, tacticsData, tacticsLoading, flags]);

  useEffect(() => {
    if (!tacticsLoading && !targetsLoading) {
      setScreenLoading(tacticsLoading);
    }
  }, [targetsLoading, tacticsLoading]);

  return {
    targets,
    tactics,
    cep,
    screenLoading,
    plans,
    defaultPlan,
    selectedAccount,
    setSelectedAccount,
    screenCepTargetsConfig,
    openedAccordion,
    renderKey,
  };
};

export default useMiViewScreenCepActivities;
